import React, { useContext, useState, useEffect } from 'react';
import { firestore } from '../../firebase/config';
import { BookingContext } from '../../context/BookingContext';
import './ServiceSelection.css';

const ServiceSelection = ({ practitionerId, presetServiceId }) => {
  const { bookingData, updateBookingData, setCurrentStep, setSelectedService: setContextService } = useContext(BookingContext);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [selectedServiceChanged, setSelectedServiceChanged] = useState(false);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // État pour le mode de consultation - initialiser depuis bookingData si existant
  const [consultationMode, setConsultationMode] = useState(
    bookingData.consultationMode || 'cabinet'
  );

  // Vérifier qu'un praticien est bien sélectionné
  useEffect(() => {
    if (!practitionerId) {
      console.warn("Aucun praticien sélectionné, redirection vers l'étape 1");
      setCurrentStep(1);
    }
  }, [practitionerId, setCurrentStep]);

  // Charger les services du praticien sélectionné
  useEffect(() => {
    const fetchServices = async () => {
      if (!practitionerId) {
        setServices([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log("Chargement des services pour le praticien:", practitionerId);

        const servicesSnapshot = await firestore
          .collection('services')
          .where('practitionerId', '==', practitionerId)
          .get();

        if (servicesSnapshot.empty) {
          console.warn("Aucun service trouvé pour ce praticien");
          setError("Aucun service disponible pour ce praticien.");
          setServices([]);
        } else {
          const allServices = servicesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          console.log("Services trouvés:", allServices);

          const activeServices = allServices.filter(service =>
            service.active !== false
          );

          if (activeServices.length === 0) {
            console.warn("Aucun service actif trouvé");
            setError("Aucun service actif disponible pour ce praticien.");
          }

          setServices(activeServices);

          // Si un service est présélectionné, le vérifier et le sélectionner
          if (presetServiceId && !selectedServiceChanged) {
            console.log("Recherche du service présélectionné:", presetServiceId);
            const presetService = activeServices.find(s => s.id === presetServiceId);

            if (presetService) {
              console.log("Service présélectionné trouvé:", presetService);
              setSelectedServiceId(presetService.id);

              // Déterminer le mode de consultation par défaut ou garder l'existant
              let defaultMode = bookingData.consultationMode || 'cabinet';

              if (!defaultMode) {
                // Choisir le premier mode disponible
                const availableModes = getAvailableModes(presetService);
                if (availableModes.includes('cabinet')) {
                  defaultMode = 'cabinet';
                } else if (availableModes.length > 0) {
                  defaultMode = availableModes[0];
                } else {
                  defaultMode = 'cabinet'; // Fallback
                }
              }

              setConsultationMode(defaultMode);

              if (!bookingData.service || bookingData.service !== presetService.id) {
                console.log("Sélection du service présélectionné avec mode:", defaultMode);
                handleServiceSelect(presetService, defaultMode);
              }
            } else {
              console.warn("Service présélectionné non trouvé ou inactif");
            }
          }
        }
      } catch (err) {
        console.error("Erreur lors du chargement des services:", err);
        setError("Impossible de charger les services du praticien.");
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [practitionerId, presetServiceId, bookingData.service]);

  // Initialiser ou réinitialiser la sélection du service
  useEffect(() => {
    if (bookingData.service) {
      const serviceExists = services.some(service => service.id === bookingData.service);

      if (!serviceExists) {
        console.warn("Le service sélectionné n'existe plus dans la liste");
        setSelectedServiceId(null);
      } else {
        setSelectedServiceId(bookingData.service);
        // Récupérer le mode de consultation depuis bookingData
        if (bookingData.consultationMode) {
          setConsultationMode(bookingData.consultationMode);
        }
      }
    } else {
      setSelectedServiceId(null);
    }
  }, [services, bookingData.service, bookingData.consultationMode]);

  const handleServiceSelect = (service, mode = null) => {
    if (!service || !service.id) {
      console.warn("Tentative de sélectionner un service invalide:", service);
      return;
    }

    // Utiliser le mode fourni ou le mode actuel
    const selectedMode = mode || consultationMode;

    // Vérifier que le mode sélectionné est disponible pour ce service
    const availableModes = getAvailableModes(service);
    if (!availableModes.includes(selectedMode)) {
      // Si le mode actuel n'est pas disponible, prendre le premier disponible
      if (availableModes.includes('cabinet')) {
        selectedMode = 'cabinet';
      } else if (availableModes.length > 0) {
        selectedMode = availableModes[0];
      } else {
        console.warn("Aucun mode de consultation disponible pour ce service");
        return;
      }
    }


    console.log("Sélection du service:", service, "avec mode:", selectedMode);

    setSelectedServiceId(service.id);

    const restTimeValue = service.restTime !== undefined && service.restTime !== null
      ? parseInt(service.restTime, 10)
      : 0;

    // Déterminer le prix selon le mode de consultation
    let currentPrice = service.price || 0;
    let homeTransportFee = 0;

    if (selectedMode === 'remote' && service.remotePrice) {
      currentPrice = service.remotePrice;
    } else if (selectedMode === 'home') {
      if (service.homePrice) {
        currentPrice = service.homePrice;
      }
      homeTransportFee = service.homeTransportFee || 0;
    }

    // Utiliser la fonction provenant du contexte pour mettre à jour le service sélectionné
    setContextService({
      id: service.id,
      title: service.title || 'Service sans titre',
      duration: service.duration || 60,
      price: currentPrice,
      remotePrice: service.remotePrice || service.price,
      homePrice: service.homePrice || service.price,
      homeTransportFee: homeTransportFee,
      homeRadius: service.homeRadius || 0,
      restTime: restTimeValue,
      notes: service.notes || '',
      canBeRemote: service.canBeRemote || false,
      remoteOnly: service.remoteOnly || false,
      canBeAtHome: service.canBeAtHome || false,
      homeOnly: service.homeOnly || false,
      consultationMode: selectedMode
    });

    // Mettre à jour les données de réservation avec le mode de consultation
    updateBookingData({
      consultationMode: selectedMode,
      isRemote: selectedMode === 'remote',
      homeTransportFee: selectedMode === 'home' ? homeTransportFee : 0
    });

    console.log("Service transmis au contexte avec mode:", selectedMode);
    setSelectedServiceChanged(true);
  };

  // Gérer le changement de mode de consultation
  const handleConsultationModeChange = (newMode) => {
    console.log("Changement de mode de consultation:", consultationMode, "->", newMode);
    setConsultationMode(newMode);

    // Si un service est sélectionné, mettre à jour son prix
    if (selectedServiceId) {
      const selectedService = services.find(s => s.id === selectedServiceId);
      if (selectedService) {
        handleServiceSelect(selectedService, newMode);
      }
    }
  };

  // Vérifier si un mode de consultation est disponible pour le service
  const isModeAvailable = (service, mode) => {
    switch (mode) {
      case 'cabinet':
        return service.canBeInCabinet !== false; // Par défaut true si pas défini
      case 'remote':
        return service.canBeRemote;
      case 'home':
        return service.canBeAtHome;
      default:
        return false;
    }
  };

  // Obtenir les modes disponibles pour un service
  const getAvailableModes = (service) => {
    const modes = [];

    if (service.canBeInCabinet !== false) modes.push('cabinet');
    if (service.canBeRemote) modes.push('remote');
    if (service.canBeAtHome) modes.push('home');
    return modes;
  };

  // Obtenir le prix pour un mode donné
  const getPriceForMode = (service, mode) => {
    switch (mode) {
      case 'remote':
        return service.remotePrice || service.price;
      case 'home':
        return (service.homePrice || service.price);
      default:
        return service.price;
    }
  };

  // Obtenir les frais de transport pour un service à domicile
  const getTransportFeeForService = (service) => {
    return service.homeTransportFee || 0;
  };

  // Obtenir le prix total pour un mode donné (incluant les frais de transport)
  const getTotalPriceForMode = (service, mode) => {
    const basePrice = getPriceForMode(service, mode);
    const transportFee = mode === 'home' ? getTransportFeeForService(service) : 0;
    return basePrice + transportFee;
  };

  const handleNext = () => {
    if (selectedServiceId) {
      console.log("Passage à l'étape 3 (calendrier) avec mode:", consultationMode);
      // S'assurer que le mode de consultation est bien persisté
      updateBookingData({
        consultationMode: consultationMode
      });
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    console.log("Retour à l'étape 1 (sélection praticien)");
    setCurrentStep(1);
  };

  if (loading) {
    return (
      <div className="service-selection loading-state">
        <div className="loading-spinner"></div>
        <p>Chargement des services...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="service-selection error-state">
        <div className="error-message">{error}</div>
        <button className="btn" onClick={handleBack}>
          Retour à la sélection du praticien
        </button>
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="service-selection empty-state">
        <p>Aucun service disponible pour ce praticien.</p>
        <button className="btn" onClick={handleBack}>
          Retour à la sélection du praticien
        </button>
      </div>
    );
  }

  const hasPreselectedService = presetServiceId && selectedServiceId === presetServiceId;
  const selectedService = services.find(s => s.id === selectedServiceId);

  return (
    <div className="service-selection">
      <h2>Choisissez votre type de consultation</h2>

      {hasPreselectedService && (
        <div className="preselected-service-notice">
          <p>Un service a été présélectionné pour vous. Vous pouvez continuer avec ce service ou en choisir un autre.</p>
        </div>
      )}

      {/* Sélection du mode de consultation si un service est sélectionné */}
      {selectedService && getAvailableModes(selectedService).length > 1 && (
        <div className="consultation-mode-section">
          <h3>Mode de consultation</h3>
          <div className="consultation-mode-simple">
            {getAvailableModes(selectedService).map(mode => (
              <label key={mode} className={`mode-button ${consultationMode === mode ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="consultationMode"
                  value={mode}
                  checked={consultationMode === mode}
                  onChange={() => handleConsultationModeChange(mode)}
                />
                {mode === 'cabinet' && '🏢 Cabinet'}
                {mode === 'remote' && '💻 Distance'}
                {mode === 'home' && '🏠 Domicile'}
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="service-options">
        {services.map((service) => {
          const availableModes = getAvailableModes(service);
          const isSelected = selectedServiceId === service.id;

          // Ne pas afficher les services sans modes de consultation disponibles
          if (availableModes.length === 0) {
            console.warn(`Service ${service.title} ignoré : aucun mode de consultation disponible`);
            return null;
          }

          return (
            <div
              key={service.id}
              className={`service-card ${isSelected ? 'selected' : ''}`}
              onClick={() => {
                const availableModes = getAvailableModes(service);

                // Si le service n'a qu'un mode disponible, le sélectionner automatiquement
                if (availableModes.length === 1) {
                  setConsultationMode(availableModes[0]);
                  handleServiceSelect(service, availableModes[0]);
                } else {
                  // Sinon, utiliser le mode actuel ou le premier disponible
                  let modeToUse = consultationMode;
                  if (!availableModes.includes(consultationMode)) {
                    modeToUse = availableModes.includes('cabinet') ? 'cabinet' : availableModes[0];
                    setConsultationMode(modeToUse);
                  }
                  handleServiceSelect(service, modeToUse);
                }
              }}
            >
              <div className="service-title">{service.title}</div>
              <div className="service-duration">Durée: {service.duration} min</div>

              {isSelected && (
                <div className="service-inline-details">
                  <span className="mode-summary">
                    {consultationMode === 'cabinet' && '🏢 Cabinet'}
                    {consultationMode === 'remote' && '💻 Distance'}
                    {consultationMode === 'home' && '🏠 Domicile'}
                    — {getTotalPriceForMode(service, consultationMode)}€
                  </span>
                  {getTransportFeeForService(service) > 0 && consultationMode === 'home' && (
                    <span className="transport-note-inline">+ {getTransportFeeForService(service)}€ déplacement</span>
                  )}
                  {service.description && (
                    <div className="service-description-mini">{service.description}</div>
                  )}
                </div>

              )}

            </div>
          );
        })}
      </div>

      <div className="step-navigation">
        <button className="btn btn-back" onClick={handleBack}>
          Retour
        </button>
        <button
          className={`btn ${!selectedServiceId ? 'btn-disabled' : ''}`}
          onClick={handleNext}
          disabled={!selectedServiceId}
        >
          Suivant
        </button>
      </div>
    </div>
  );
};

export default ServiceSelection;